import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Square, CheckCircle, XCircle, Loader2, RotateCcw, Clock } from 'lucide-react';
import { Modal } from './ui/Modal';
import OsScroll from './ui/OsScroll';
import { flattenCollectionRequests, runCollectionRequests } from '../utils/collectionRunner';
import { recordFeature, ACTION } from '../services/telemetry';
import { runnerSendOptions } from '../services/runnerSendOptions';

function methodColor(method) {
  switch ((method || 'GET').toUpperCase()) {
    case 'GET':     return '#22c55e';
    case 'POST':    return '#f59e0b';
    case 'PUT':     return '#f97316';
    case 'DELETE':  return '#ef4444';
    case 'PATCH':   return '#a855f7';
    case 'HEAD':    return '#60a5fa';
    default:        return '#6b7280';
  }
}

export default function CollectionRunnerModal({
  isOpen,
  onClose,
  collection,
  folderId = null,
  autoRun = false,
  onRunComplete,
  selectedEnvironment,
  collectionVariables,
  workspaceVariables,
  globalVariables,
}) {
  const [runStatus, setRunStatus] = useState('idle');
  const [results, setResults] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const cancelledRef = useRef(false);
  const autoRunTriggeredRef = useRef(false);

  const requests = collection ? flattenCollectionRequests(collection, { folderId }) : [];

  const runAll = useCallback(async () => {
    if (requests.length === 0) return;
    recordFeature(ACTION.COLLECTION_RUN);   // identifier-free: the runner was used today
    cancelledRef.current = false;
    setRunStatus('running');
    setResults([]);
    setCurrentIndex(0);

    const newResults = await runCollectionRequests({
      collection,
      selectedEnvironment,
      collectionVariables,
      workspaceVariables,
      globalVariables,
      folderId,
      ...runnerSendOptions(),
      onProgress: ({ index, results: partial }) => {
        if (cancelledRef.current) return;
        setCurrentIndex(index);
        setResults(partial);
      },
      signal: { get aborted() { return cancelledRef.current; } },
    });

    if (!cancelledRef.current) {
      setResults(newResults);
    }
    setRunStatus('done');
    setCurrentIndex(-1);

    onRunComplete?.({
      results: newResults,
      cancelled: cancelledRef.current,
    });
  }, [
    collection,
    collectionVariables,
    folderId,
    globalVariables,
    onRunComplete,
    requests.length,
    selectedEnvironment,
    workspaceVariables,
  ]);

  useEffect(() => {
    if (!isOpen) {
      autoRunTriggeredRef.current = false;
      return;
    }
    if (autoRun && !autoRunTriggeredRef.current && requests.length > 0 && runStatus === 'idle') {
      autoRunTriggeredRef.current = true;
      runAll();
    }
  }, [autoRun, isOpen, requests.length, runAll, runStatus]);

  const handleStop = () => { cancelledRef.current = true; };

  const handleClose = () => {
    cancelledRef.current = true;
    setRunStatus('idle');
    setResults([]);
    setCurrentIndex(-1);
    onClose();
  };

  const passedCount = results.filter((r) => r.passed).length;
  const failedCount = results.filter((r) => !r.passed).length;
  const totalTime = results.reduce((acc, r) => acc + (r.time || 0), 0);

  const passedPct = requests.length > 0 ? (passedCount / requests.length) * 100 : 0;
  const failedPct = requests.length > 0 ? (failedCount / requests.length) * 100 : 0;
  const passedDisplayPct = requests.length > 0
    ? Math.round((passedCount / requests.length) * 100)
    : 0;

  if (!collection) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Run collection"
      subtitle={collection.name}
      size="xl"
      customIcon={<Play size={22} className="text-green-600" />}
      preventBackdropClose={runStatus === 'running'}
      preventEscapeClose={runStatus === 'running'}
    >
      {/* Stats card — shown while running or done */}
      {runStatus !== 'idle' && requests.length > 0 && (
        <div className="crm-stats-card">
          <div className="crm-stats-header">
            <span className="crm-stats-count">
              {results.length} / {requests.length} requests
            </span>
            <span className="crm-stats-pct">{passedDisplayPct}% passed</span>
          </div>

          {/* Two-colour bar */}
          <div className="crm-progress-track">
            <div className="crm-progress-green" style={{ width: `${passedPct}%` }} />
            <div className="crm-progress-red"   style={{ width: `${failedPct}%` }} />
          </div>

          {/* Badges */}
          <div className="crm-badges">
            <span className="crm-badge crm-badge-pass">
              <CheckCircle size={11} />
              {passedCount}&nbsp; passed
            </span>
            <span className="crm-badge crm-badge-fail">
              <XCircle size={11} />
              {failedCount}&nbsp; failed
            </span>
            <span className="crm-badge crm-badge-time">
              <Clock size={11} />
              {totalTime}&nbsp; ms total
            </span>
          </div>
        </div>
      )}

      {/* Request table */}
      {requests.length === 0 ? (
        <div className="crm-empty">No requests in this collection.</div>
      ) : (
        <OsScroll className="crm-table-wrap" defer>
          <table className="crm-table">
            <thead>
              <tr>
                <th className="crm-th" style={{ width: 28 }}>#</th>
                <th className="crm-th" style={{ width: 72 }}>Method</th>
                <th className="crm-th">Name / URL</th>
                <th className="crm-th crm-th-right" style={{ width: 64 }}>Status</th>
                <th className="crm-th crm-th-right" style={{ width: 72 }}>Time</th>
                <th className="crm-th crm-th-center" style={{ width: 36 }} />
              </tr>
            </thead>
            <tbody>
              {requests.map((req, i) => {
                const result = results[i];
                const isActive = runStatus === 'running' && i === currentIndex;
                const isFailed = result && !result.passed;

                return (
                  <tr
                    key={req.id || i}
                    className={`crm-row${isActive ? ' crm-row--active' : ''}${isFailed ? ' crm-row--fail' : ''}`}
                  >
                    <td className="crm-td-num">{i + 1}</td>

                    <td className="crm-td" style={{ width: 72 }}>
                      <span
                        className="crm-method"
                        style={{ color: methodColor(req.method), fontWeight: 600, fontSize: '0.625rem' }}
                      >
                        {req.method || 'GET'}
                      </span>
                    </td>

                    <td className="crm-td" style={{ maxWidth: 0 }}>
                      <div className="crm-name">{req.name || req.url || 'Unnamed'}</div>
                      {req.name && <div className="crm-url">{req.url}</div>}
                    </td>

                    <td className="crm-td crm-td-right">
                      {result ? (
                        <span className={result.passed ? 'crm-status-pass' : 'crm-status-fail'}>
                          {result.status ?? 'ERR'}
                        </span>
                      ) : isActive ? (
                        <Loader2 size={13} className="crm-spinner" style={{ color: '#60a5fa', marginLeft: 'auto' }} />
                      ) : (
                        <span className="crm-status-dash">—</span>
                      )}
                    </td>

                    <td className="crm-td crm-td-right">
                      <span className="crm-time">{result ? `${result.time}ms` : '—'}</span>
                    </td>

                    <td className="crm-td crm-td-center">
                      {result && (
                        result.passed
                          ? <CheckCircle size={13} style={{ color: '#22c55e', display: 'block', margin: '0 auto' }} />
                          : <XCircle size={13} style={{ color: '#ef4444', display: 'block', margin: '0 auto' }} title={
                              result.error ||
                              (result.tests?.filter((t) => !t.passed).map((t) => `✗ ${t.name}${t.error ? `: ${t.error}` : ''}`).join('\n')) ||
                              `HTTP ${result.status}`
                            } />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </OsScroll>
      )}

      {/* Footer */}
      <div className="crm-footer">
        {runStatus === 'done' && (
          <button
            type="button"
            className="crm-btn-reset"
            onClick={() => { setRunStatus('idle'); setResults([]); setCurrentIndex(-1); }}
          >
            <RotateCcw size={14} />
            Reset
          </button>
        )}

        {runStatus === 'running' && (
          <button type="button" className="crm-btn-stop" onClick={handleStop}>
            <Square size={13} />
            Stop
          </button>
        )}

        {runStatus !== 'running' && requests.length > 0 && (
          <button type="button" className="crm-btn-run" onClick={runAll}>
            <Play size={13} />
            {runStatus === 'done' ? 'Run again' : 'Run All'}
          </button>
        )}
      </div>
    </Modal>
  );
}
